import { useEffect, useState } from "react";
import { apiRequest } from "common/utils/api";
import { roleAccessEnum } from "common/enum/RoleAccessEnum";
import { REQUEST_METHOD } from "common/enum/RequestMethod";
import { isClubOwner } from "common/store/userSession";

const DEFAULT_ACCESS = {
  home: true,
  club: false,
  staff: false,
  role: false,
  reservation: false,
  coupons: false,
  sales: false,
  membership: false,
  client: false,
  staffAll: false,
};

export const useAccessManager = (club, pathname, search, refreshToken) => {
  const [accessLoaded, setAccessLoaded] = useState(false);
  const [roleName, setRoleName] = useState("");
  const [clubName, setClubName] = useState("");
  const [clubImageRef, setClubImageRef] = useState(
    localStorage.getItem("clubImageRef") || null,
  );
  const [prevClub, setPrevClub] = useState(club);
  const [access, setAccess] = useState(DEFAULT_ACCESS);

  if (club !== prevClub) {
    setPrevClub(club);
    setAccessLoaded(false);
  }

  // role/access 관련 - pathname, search 변경 시에도 재평가
  useEffect(() => {
    let cancelled = false;

    const getUserRole = async () => {
      try {
        const res = await apiRequest({
          method: REQUEST_METHOD.GET,
          url: "/api/v1.0/users/role?clubId=" + club,
        });
        return res.data.result;
      } catch {
        setRoleName("");
        return null;
      }
    };

    const getAdmin = async () => {
      try {
        const res = await apiRequest({
          method: REQUEST_METHOD.GET,
          url: "/api/v1.0/sign-in/isAdmin",
        });
        return res?.data?.result?.isAdmin;
      } catch {
        return false;
      }
    };

    const checkRoleAccess = (list = []) => {
      const flags = {
        isStaffList: false,
        isRoleSetting: false,
        isReservation: false,
        isCoupons: false,
        isSales: false,
        isClubInformation: false,
        isMembershipAccess: false,
      };
      list.forEach(({ accessCode }) => {
        if (accessCode === roleAccessEnum.STAFF_LIST) flags.isStaffList = true;
        if (accessCode === roleAccessEnum.ROLE_SETTING)
          flags.isRoleSetting = true;
        if (accessCode === roleAccessEnum.RESERVATION)
          flags.isReservation = true;
        if (accessCode === roleAccessEnum.COUPONS) flags.isCoupons = true;
        if (accessCode === roleAccessEnum.SALES) flags.isSales = true;
        if (accessCode === roleAccessEnum.CLUB_INFORMATION)
          flags.isClubInformation = true;
        if (accessCode === roleAccessEnum.MEMBERSHIP_MANAGEMENT)
          flags.isMembershipAccess = true;
      });
      return flags;
    };

    const initRole = async () => {
      if (!club) {
        console.log("[initRole] club 없음 → DEFAULT 리셋");
        setAccess(DEFAULT_ACCESS);
        setRoleName("");
        setAccessLoaded(true);
        return;
      }
      setAccessLoaded(false);

      try {
        // /home, /staff-all, ?pageType=staff-all 진입 시 특수 처리
        const searchParams = new URLSearchParams(search || "");
        const pageType = searchParams.get("pageType");
        const isStaffAllContext =
          pathname === "/home" ||
          pathname === "/staff-all" ||
          pageType === "staff-all";

        if (isStaffAllContext) {
          if (cancelled) return;
          setAccess({
            home: true,
            club: false,
            staff: false,
            role: false,
            reservation: false,
            coupons: false,
            sales: false,
            membership: false,
            client: false,
            staffAll: isClubOwner(),
          });
          return;
        }

        const isAdmin = await getAdmin();
        if (isAdmin) {
          if (cancelled) return;
          setRoleName("admin");
          setAccess({
            home: true,
            club: true,
            staff: true,
            role: true,
            reservation: true,
            coupons: true,
            sales: true,
            membership: true,
            client: true,
            staffAll: false,
          });
          return;
        }

        const userRole = await getUserRole();
        if (cancelled) return;

        if (!(userRole && userRole.list?.length > 0)) {
          setRoleName("");
          setAccess({
            home: true,
            club: false,
            staff: false,
            role: false,
            reservation: false,
            coupons: false,
            sales: false,
            membership: false,
            client: false,
            staffAll: false,
          });
          return;
        }

        setRoleName(userRole.list[0].roleName);

        const isClubManager =
          userRole.roleAccessCnt === userRole.roleAccessKindCnt;

        if (isClubManager) {
          setAccess({
            home: true,
            club: true,
            staff: true,
            role: true,
            reservation: true,
            coupons: true,
            sales: true,
            membership: true,
            client: false,
            staffAll: false,
          });
        } else {
          const {
            isStaffList,
            isRoleSetting,
            isReservation,
            isCoupons,
            isSales,
            isClubInformation,
            isMembershipAccess,
          } = checkRoleAccess(userRole.list);
          setAccess({
            home: true,
            club: isClubInformation,
            staff: isStaffList,
            role: isRoleSetting,
            reservation: isReservation,
            coupons: isCoupons,
            sales: isSales,
            membership: isMembershipAccess,
            client: false,
            staffAll: false,
          });
        }
      } finally {
        if (!cancelled) setAccessLoaded(true);
      }
    };

    initRole();

    return () => {
      cancelled = true;
    };
  }, [club, pathname, search]);

  // clubName/clubImageRef - club 변경 시에만 호출
  useEffect(() => {
    if (!club) {
      setClubName("");
      setClubImageRef(null);
      localStorage.removeItem("clubImageRef");
      return;
    }

    apiRequest({
      method: REQUEST_METHOD.GET,
      url: "/api/v2.0/club?clubId=" + club,
    })
      .then((res) => {
        const clubData = res?.data?.club;
        if (!clubData) {
          setClubName("");
          setClubImageRef(null);
          return;
        }
        const attachmentRef = clubData?.attachmentRefs?.[0];
        const imageRef =
          attachmentRef?.attachmentRef ?? attachmentRef?.imageRef ?? null;
        setClubName(clubData.name);
        setClubImageRef(imageRef);

        localStorage.setItem("clubId", clubData.id);
        localStorage.setItem("country", clubData.country);
        localStorage.setItem("currency", clubData.currencyCode);
        localStorage.setItem("clubName", clubData.name);
        localStorage.setItem("operatingHoles", clubData.operatingHoles ?? 9);
        if (imageRef) {
          localStorage.setItem("clubImageRef", imageRef);
        } else {
          localStorage.removeItem("clubImageRef");
        }
      })
      .catch(() => {
        setClubName("");
      });
    // refreshToken 변경 시 재조회 (예: 골프 정보 저장 후 갱신)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club, refreshToken]);

  return { accessLoaded, roleName, access, clubName, clubImageRef };
};
